/**
 * Visualization Registry
 * 
 * This file contains metadata for all visualizations in the SciVizHub project.
 * Add new visualizations here to make them available throughout the application.
 */

// Categories for visualizations
export const CATEGORIES = {
  PROBABILITY: 'probability',
  CALCULUS: 'calculus',
  PHYSICS: 'physics',
  ENGINEERING: 'engineering',
  LINEAR_ALGEBRA: 'linear-algebra',
  STATISTICS: 'statistics',
  COMPUTER_SCIENCE: 'computer-science',
  CHEMISTRY: 'chemistry',
  BIOLOGY: 'biology',
  MATHEMATICS: 'mathematics',
};

// Difficulty levels
export const DIFFICULTY = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
};

// List of all visualizations
const visualizations = [
  {
    id: 'bayes-theorem',
    title: 'Bayes\' Theorem',
    shortDescription: 'Understand the fundamentals of conditional probability through interactive visual explanations.',
    fullDescription: `Bayes' theorem is a fundamental concept in probability theory that describes how to update our beliefs
    based on new evidence. This visualization helps you understand Bayes' theorem through multiple perspectives:
    a contingency table, a tree diagram, and a Venn diagram. Use the interactive controls to see how different
    variables affect the probabilities.`,
    route: '/visualization/bayes-theorem',
    thumbnail: process.env.PUBLIC_URL + '/images/bayes-thumbnail.jpg',
    category: CATEGORIES.PROBABILITY,
    tags: ['probability', 'statistics', 'conditional probability', 'bayes theorem'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-03-08',
    featured: true,
    relatedVisualizations: [], // Will contain ids of related visualizations when more are added
    prerequisites: [], // Concepts to understand before this visualization
    concepts: ['Conditional Probability', 'Probability Theory', 'Statistical Inference'],
  },

  {
    id: 'monty-hall',
    title: 'Monty Hall Simulator',
    shortDescription: 'Test stay vs switch strategies and see conditional probability emerge from repeated trials.',
    fullDescription: `The Monty Hall problem is a classic conditional probability puzzle. This interactive simulator lets you play rounds manually and run large randomized experiments to compare outcomes for staying with your first choice versus switching after the host reveals a goat. Track empirical win rates and build intuition for why switching wins about two-thirds of the time.`,
    route: '/visualization/monty-hall',
    thumbnail: process.env.PUBLIC_URL + '/images/monty-hall-thumbnail.png',
    category: CATEGORIES.PROBABILITY,
    tags: ['probability', 'conditional probability', 'simulation', 'decision making', 'statistics'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-02-12',
    featured: true,
    relatedVisualizations: ['bayes-theorem', 'central-limit-theorem'],
    prerequisites: ['Basic probability concepts'],
    concepts: ['Conditional Probability', 'Information Update', 'Simulation', 'Long-Run Frequency'],
  },

  {
    id: 'integral-accumulation',
    title: 'Integral Accumulation Visualizer',
    shortDescription: 'Approximate definite integrals with Riemann sums and compare to exact values.',
    fullDescription: `This interactive calculus visualization demonstrates how definite integrals can be approximated by partitioning an interval into subintervals and summing geometric contributions. Compare left, right, midpoint, and trapezoidal methods while tracking approximation error against exact integral values from antiderivatives.`,
    route: '/visualization/integral-accumulation',
    thumbnail: process.env.PUBLIC_URL + '/images/integral-accumulation-thumbnail.png',
    category: CATEGORIES.CALCULUS,
    tags: ['calculus', 'integrals', 'riemann sums', 'numerical methods', 'area under curve'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-02-12',
    featured: true,
    relatedVisualizations: ['derivative-explorer'],
    prerequisites: ['Basic functions and graph interpretation'],
    concepts: ['Definite Integral', 'Riemann Sum', 'Trapezoidal Rule', 'Approximation Error'],
  },

  {
    id: 'sorting-algorithms',
    title: 'Sorting Algorithms',
    shortDescription: 'Visualize and compare different sorting algorithms to understand their efficiency and mechanisms.',
    fullDescription: `This interactive visualization demonstrates how various sorting algorithms work by animating the
    process of arranging elements. Compare algorithms like Bubble Sort, Selection Sort, Insertion Sort, Merge Sort,
    and Quick Sort to see their performance characteristics and understand algorithmic complexity in an intuitive way.`,
    route: '/visualization/sorting-algorithms',
    thumbnail: process.env.PUBLIC_URL + '/images/sorting-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['algorithms', 'sorting', 'computer science', 'data structures', 'visualization'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-03-10',
    featured: true,
    relatedVisualizations: ['bayes-theorem'], // Connect related visualizations
    prerequisites: ['Basic programming concepts'],
    concepts: ['Algorithm Complexity', 'Comparison Sorting', 'Time Complexity', 'Space Complexity'],
  },

  {
    id: 'central-limit-theorem',
    title: 'Central Limit Theorem',
    shortDescription: 'Explore how sample means converge to a normal distribution regardless of the original population distribution.',
    fullDescription: `The Central Limit Theorem is a fundamental concept in statistics that explains why normal distributions are so common in nature. This interactive visualization lets you select different population distributions (uniform, exponential, bimodal, etc.) and observe how the distribution of sample means approaches a normal distribution as sample size increases.`,
    route: '/visualization/central-limit-theorem',
    thumbnail: process.env.PUBLIC_URL + '/images/clt-thumbnail.jpg', // You'll need to create this image
    // category: CATEGORIES.STATISTICS,
    category: CATEGORIES.PROBABILITY,
    tags: ['statistics', 'probability', 'normal distribution', 'sampling', 'inferential statistics'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-03-12',
    featured: true,
    relatedVisualizations: ['bayes-theorem'], // Connect related visualizations
    prerequisites: ['Basic statistics concepts'],
    concepts: ['Sampling Distribution', 'Normal Distribution', 'Statistical Inference', 'Standard Error'],
  },

  {
    id: 'pathfinding-algorithms',
    title: 'Pathfinding Algorithms',
    shortDescription: 'Visualize how algorithms like A* and Dijkstras find optimal paths through mazes and obstacles.',
    fullDescription: `This interactive visualization demonstrates how different pathfinding algorithms work by animating
    their search process through a customizable grid. Compare algorithms like Dijkstra's, A*, Breadth-First Search, 
    Depth-First Search, and Greedy Best-First Search to understand their efficiency, completeness, and optimality properties.
    Create walls, move start and end points, and watch in real-time as the algorithms navigate the terrain.`,
    route: '/visualization/pathfinding-algorithms',
    thumbnail: process.env.PUBLIC_URL + '/images/pathfinding-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['algorithms', 'pathfinding', 'computer science', 'graph theory', 'artificial intelligence', 'maze solving'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-03-15',
    featured: true,
    relatedVisualizations: ['sorting-algorithms'], // Connect related visualizations
    prerequisites: ['Basic graph theory concepts'],
    concepts: ['Graph Traversal', 'Heuristics', 'Algorithm Complexity', 'Shortest Path Problems'],
  },

  {
    id: 'pendulum-wave',
    title: 'Pendulum Wave Dynamics',
    shortDescription: 'Explore mesmerizing wave patterns created by pendulums of different lengths moving in and out of phase.',
    fullDescription: `This visualization demonstrates the fascinating pendulum wave effect, where a series of pendulums with
    precisely calculated lengths create beautiful wave patterns as they move in and out of phase with each other.
    Watch as the pendulums start in alignment, then create traveling waves, standing waves, and complex patterns before
    returning to their original formation. Adjust parameters like pendulum count, length, and cycle time to explore
    how these factors affect the patterns that emerge.`,
    route: '/visualization/pendulum-wave',
    thumbnail: process.env.PUBLIC_URL + '/images/pendulum-wave-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'waves', 'oscillation', 'harmonic motion', 'phase', 'resonance', 'interference'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-03-18',
    featured: true,
    relatedVisualizations: [], // Connect related visualizations when available
    prerequisites: ['Basic understanding of waves and oscillation'],
    concepts: ['Simple Harmonic Motion', 'Phase Relationships', 'Wave Interference', 'Resonance'],
  },

  {
    id: 'projectile-motion-lab',
    title: 'Projectile Motion Lab',
    shortDescription: 'Explore how angle, speed, gravity, drag, and wind shape projectile trajectories.',
    fullDescription: `This interactive physics lab visualizes projectile trajectories under different launch conditions and environments. Adjust launch angle, speed, gravity, wind, and drag to observe how these parameters influence range, flight time, and peak height. Compare numerical simulation output with a no-drag analytic baseline to build intuition about model assumptions and real-world effects.`,
    route: '/visualization/projectile-motion-lab',
    thumbnail: process.env.PUBLIC_URL + '/images/projectile-motion-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'kinematics', 'projectile motion', 'drag', 'gravity', 'simulation'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-02-12',
    featured: true,
    relatedVisualizations: ['pendulum-wave'],
    prerequisites: ['Basic algebra and coordinate graphs'],
    concepts: ['Kinematics', 'Forces', 'Air Resistance', 'Trajectory Analysis'],
  },

  {
    id: 'compression-algorithms',
    title: 'Data Compression Algorithms',
    shortDescription: 'Visualize how compression algorithms like Huffman, RLE, and LZW reduce data size through smart encoding.',
    fullDescription: `This interactive visualization demonstrates how different compression algorithms work to reduce data size 
    by identifying and encoding patterns more efficiently. Explore Run-Length Encoding, Huffman Coding, and LZW compression 
    through step-by-step animations that reveal the inner workings of these fundamental data processing techniques.`,
    route: '/visualization/compression-algorithms',
    thumbnail: process.env.PUBLIC_URL + '/images/compression-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['compression', 'algorithms', 'data structures', 'information theory', 'huffman coding', 'run-length encoding', 'lzw'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-04-05',
    featured: true,
    relatedVisualizations: ['sorting-algorithms', 'pathfinding-algorithms'], // Connect to related visualizations
    prerequisites: ['Basic understanding of data representation'],
    concepts: ['Data Compression', 'Information Theory', 'Lossless Compression', 'Entropy', 'Variable-Length Coding'],
  },


  {
    id: 'fourier-transform',
    title: 'Fourier Transform',
    shortDescription: 'Explore how complex signals can be decomposed into simple sine waves of different frequencies.',
    fullDescription: `The Fourier Transform is a powerful mathematical technique that decomposes complex signals into a sum of simple
    sine waves. This interactive visualization lets you see how different signals (square waves, sawtooth, etc.) can be built
    from sine waves of various frequencies, amplitudes, and phases. Draw your own custom signals and watch in real-time as 
    they're synthesized and decomposed, gaining intuition for this fundamental concept in signal processing.`,
    route: '/visualization/fourier-transform',
    thumbnail: process.env.PUBLIC_URL + '/images/fourier-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.ENGINEERING,
    tags: ['signal processing', 'wave theory', 'fourier analysis', 'frequency domain', 'harmonics', 'mathematics'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-04-15',
    featured: true,
    relatedVisualizations: ['pendulum-wave'], // Connect to related visualizations
    prerequisites: ['Basic trigonometry'],
    concepts: ['Signal Decomposition', 'Frequency Domain', 'Harmonic Analysis', 'Signal Synthesis', 'Spectrum Analysis'],
  },

  {
    id: 'neural-network-training',
    title: 'Neural Network Training',
    shortDescription: 'Watch a simple neural network learn to classify data in real-time, visualizing how decision boundaries evolve during training.',
    fullDescription: `This interactive visualization demonstrates how neural networks learn patterns from data through training.
    Observe as a neural network adapts its decision boundary to classify different datasets, from simple geometric 
    patterns to complex non-linear distributions. Adjust the network architecture, learning parameters, and dataset
    properties to gain an intuitive understanding of how these powerful algorithms work.`,
    route: '/visualization/neural-network-training',
    thumbnail: process.env.PUBLIC_URL + '/images/neural-network-thumbnail.jpg',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['machine learning', 'neural networks', 'artificial intelligence', 'classification', 'deep learning'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-05-01',
    featured: true,
    relatedVisualizations: ['pathfinding-algorithms', 'sorting-algorithms'],
    prerequisites: ['Basic understanding of machine learning concepts'],
    concepts: ['Neural Networks', 'Supervised Learning', 'Gradient Descent', 'Classification', 'Decision Boundaries'],
  },

  {
    id: 'traveling-salesman',
    title: 'Traveling Salesman Problem',
    shortDescription: 'Explore different heuristic approaches (nearest neighbor, 2-opt, genetic algorithms) to solving this classic NP-hard optimization problem.',
    fullDescription: `The Traveling Salesman Problem (TSP) asks: "Given a list of cities and the distances between them, what is the shortest possible route that visits each city exactly once and returns to the origin?" This interactive visualization demonstrates several heuristic approaches to this computationally challenging problem, comparing their effectiveness and tradeoffs.`,
    route: '/visualization/traveling-salesman',
    thumbnail: process.env.PUBLIC_URL + '/images/tsp-thumbnail.jpg', // Will need to create this image
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['algorithms', 'optimization', 'np-hard', 'graph theory', 'computer science', 'heuristics', 'operations research'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-05-01',
    featured: true,
    relatedVisualizations: ['pathfinding-algorithms', 'sorting-algorithms'], // Connect to related visualizations
    prerequisites: ['Basic understanding of algorithms and optimization'],
    concepts: ['NP-Hard Problems', 'Heuristic Algorithms', 'Optimization', 'Graph Theory', 'Genetic Algorithms'],
  },

  {
    id: 'maze-generation',
    title: 'Maze Generation Algorithms',
    shortDescription: 'Visualize different techniques for generating mazes including recursive backtracking, Kruskal\'s algorithm, Prim\'s algorithm, and recursive division.',
    fullDescription: `Maze generation algorithms transform a simple grid into complex labyrinths through systematic processes of adding or removing walls. This visualization demonstrates several approaches to maze generation, showing how each algorithm creates mazes with distinct characteristics and visual patterns. Watch the step-by-step maze creation process and compare the resulting structures.`,
    route: '/visualization/maze-generation',
    thumbnail: process.env.PUBLIC_URL + '/images/maze-generation-thumbnail.jpg', // Will need to create this image
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['algorithms', 'maze generation', 'graph theory', 'computer science', 'procedural generation', 'spanning trees', 'recursive algorithms'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-05-15',
    featured: true,
    relatedVisualizations: ['pathfinding-algorithms', 'traveling-salesman'], // Connect to related visualizations
    prerequisites: ['Basic understanding of algorithms and data structures'],
    concepts: ['Graph Theory', 'Spanning Trees', 'Recursive Algorithms', 'Procedural Generation', 'Minimum Spanning Trees'],
  },

  {
    id: 'matrix-transformation',
    title: 'Matrix Transformation Visualizer',
    shortDescription: 'Explore how matrices transform space by visualizing various linear transformations in 2D and 3D.',
    fullDescription: `This interactive visualization demonstrates how matrices transform space, allowing you to visualize 
    linear transformations such as scaling, rotation, reflection, and shearing. Experiment with different matrices 
    in both 2D and 3D modes, observe how they affect the coordinate grid and unit shapes, and create animation 
    sequences to understand transformation composition.`,
    route: '/visualization/matrix-transformation',
    thumbnail: process.env.PUBLIC_URL + '/images/matrix-transformation-thumbnail.jpg', // You'll need to create this image
    category: CATEGORIES.LINEAR_ALGEBRA,
    tags: ['linear algebra', 'matrices', 'transformations', 'computer graphics', 'mathematics', 'vectors'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2023-06-01',
    featured: true,
    relatedVisualizations: ['fourier-transform'], // Connect to related visualizations
    prerequisites: ['Basic understanding of matrices and vectors'],
    concepts: ['Linear Transformations', 'Matrix Operations', 'Vector Spaces', 'Basis Vectors', 'Coordinate Systems'],
  },

  {
    id: 'eigen-geometry-explorer',
    title: 'Eigenvector and Eigenvalue Geometry Explorer',
    shortDescription: 'Visualize invariant directions, scaling factors, and geometric effects of 2x2 matrices.',
    fullDescription: `This linear algebra visualization connects matrix algebra to geometry by showing how transformations act on vectors, grids, and the unit circle. Explore determinant, trace, and eigen-structure in real time. Identify eigenvector directions when they exist, inspect repeated or defective cases, and compare behavior under complex eigenvalues where no real invariant directions appear.`,
    route: '/visualization/eigen-geometry-explorer',
    thumbnail: process.env.PUBLIC_URL + '/images/eigen-geometry-thumbnail_2.png',
    category: CATEGORIES.LINEAR_ALGEBRA,
    tags: ['linear algebra', 'eigenvalues', 'eigenvectors', 'matrix transformations', 'geometry'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-02-12',
    featured: true,
    relatedVisualizations: ['matrix-transformation'],
    prerequisites: ['Basic matrix multiplication and vectors'],
    concepts: ['Eigenvalues', 'Eigenvectors', 'Determinant', 'Trace', 'Invariant Directions'],
  },

  {
    id: 'derivative-explorer',
    title: 'Derivative Explorer',
    shortDescription: 'Explore the geometric meaning of derivatives as the slope of tangent lines with interactive visualizations.',
    fullDescription: `This interactive visualization demonstrates the fundamental concept of derivatives in calculus through a 
    geometric perspective. Explore how the derivative represents the slope of the tangent line at any point on a curve. 
    Drag points along various functions to see tangent lines update in real-time, toggle between different function types 
    (polynomial, trigonometric, exponential), visualize the limit definition with secant lines approaching the tangent, 
    and use the zooming feature to observe how functions appear increasingly linear at smaller scales.`,
    route: '/visualization/derivative-explorer',
    thumbnail: process.env.PUBLIC_URL + '/images/derivative-explorer-thumbnail.png',
    category: CATEGORIES.CALCULUS,
    tags: ['calculus', 'derivatives', 'tangent lines', 'slopes', 'limits'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-12',
    featured: true,
    relatedVisualizations: ['fourier-transform'], 
    prerequisites: ['Basic understanding of functions and graphs'],
    concepts: ['Derivatives', 'Tangent Lines', 'Limit Definition', 'Local Linearity'],
  },

  {
    id: 'wave-interference',
    title: 'Wave Interference Simulator',
    shortDescription: 'Explore constructive and destructive interference patterns with an interactive ripple tank simulation.',
    fullDescription: `Wave interference occurs when two or more waves overlap, creating patterns of constructive and destructive interference. This interactive ripple tank lets you place multiple wave sources, adjust frequency and wavelength, and watch stunning interference patterns form in real-time. Explore the double-slit experiment and discover why interference is central to understanding light, sound, and quantum mechanics.`,
    route: '/visualization/wave-interference',
    thumbnail: process.env.PUBLIC_URL + '/images/wave-interference-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'waves', 'interference', 'double-slit', 'superposition', 'optics'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-16',
    featured: true,
    relatedVisualizations: ['pendulum-wave', 'fourier-transform'],
    prerequisites: ['Basic understanding of waves'],
    concepts: ['Wave Superposition', 'Constructive Interference', 'Destructive Interference', 'Double-Slit Experiment'],
  },

  {
    id: 'double-pendulum',
    title: 'Double Pendulum & Chaos Theory',
    shortDescription: 'Watch chaotic motion unfold as a double pendulum traces beautiful, unpredictable trails through space.',
    fullDescription: `The double pendulum is one of the simplest physical systems that exhibits chaotic behavior. This visualization simulates a pendulum attached to the end of another pendulum, producing wildly unpredictable motion from even tiny changes in initial conditions. Watch mesmerizing color trails, compare two nearly identical pendulums diverging into chaos, and explore the mathematics of sensitive dependence on initial conditions.`,
    route: '/visualization/double-pendulum',
    thumbnail: process.env.PUBLIC_URL + '/images/double-pendulum-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'chaos theory', 'pendulum', 'nonlinear dynamics', 'Lagrangian mechanics'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-16',
    featured: true,
    relatedVisualizations: ['pendulum-wave', 'wave-interference'],
    prerequisites: ['Basic physics concepts'],
    concepts: ['Chaos Theory', 'Sensitive Dependence', 'Lagrangian Mechanics', 'Energy Conservation'],
  },

  {
    id: 'epidemic-sir',
    title: 'Epidemic SIR Model Simulator',
    shortDescription: 'Simulate disease spread through a population and explore how interventions like vaccination and social distancing flatten the curve.',
    fullDescription: `The SIR (Susceptible-Infected-Recovered) model is the foundation of mathematical epidemiology. This interactive simulator combines an agent-based population view with real-time SIR curves, letting you visualize how diseases spread and how interventions like vaccination, social distancing, and quarantine affect outcomes. Experiment with different parameters to understand concepts like R0, herd immunity, and curve flattening.`,
    route: '/visualization/epidemic-sir',
    thumbnail: process.env.PUBLIC_URL + '/images/epidemic-sir-thumbnail.png',
    category: CATEGORIES.BIOLOGY,
    tags: ['biology', 'epidemiology', 'SIR model', 'disease spread', 'simulation', 'public health'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-16',
    featured: true,
    relatedVisualizations: ['neural-network-training'],
    prerequisites: ['Basic understanding of rates and populations'],
    concepts: ['SIR Model', 'R0', 'Herd Immunity', 'Epidemic Dynamics', 'Intervention Strategies'],
  },

  {
    id: 'reaction-kinetics',
    title: 'Chemical Reaction Kinetics',
    shortDescription: 'Visualize molecular collisions, reaction rates, and chemical equilibrium with an animated particle simulation.',
    fullDescription: `Explore the world of chemical kinetics through an interactive particle simulation. Watch molecules collide and react, observe how temperature affects reaction rates through the Arrhenius equation, and discover Le Chatelier's principle by perturbing systems at equilibrium. This visualization bridges the gap between microscopic molecular behavior and macroscopic concentration curves.`,
    route: '/visualization/reaction-kinetics',
    thumbnail: process.env.PUBLIC_URL + '/images/reaction-kinetics-thumbnail.jpg',
    category: CATEGORIES.CHEMISTRY,
    tags: ['chemistry', 'kinetics', 'equilibrium', 'Arrhenius', 'catalysis', 'Le Chatelier'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-16',
    featured: true,
    relatedVisualizations: ['epidemic-sir'],
    prerequisites: ['Basic chemistry concepts'],
    concepts: ['Reaction Rates', 'Arrhenius Equation', 'Chemical Equilibrium', 'Le Chatelier Principle', 'Catalysis'],
  },

  {
    id: 'fractal-explorer',
    title: 'Fractal Explorer',
    shortDescription: 'Dive into the infinite complexity of the Mandelbrot set, Julia sets, and the Burning Ship fractal with interactive zoom.',
    fullDescription: `Fractals are infinitely complex patterns that are self-similar across different scales. This interactive explorer lets you navigate the Mandelbrot set, Julia sets, and the Burning Ship fractal with click-to-zoom navigation and multiple stunning color schemes. Discover the beautiful boundary between mathematical order and chaos, where simple equations produce infinitely detailed structures.`,
    route: '/visualization/fractal-explorer',
    thumbnail: process.env.PUBLIC_URL + '/images/fractal-explorer-thumbnail.png',
    category: CATEGORIES.MATHEMATICS,
    tags: ['mathematics', 'fractals', 'Mandelbrot', 'Julia set', 'complex numbers', 'chaos'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-16',
    featured: true,
    relatedVisualizations: ['double-pendulum'],
    prerequisites: [],
    concepts: ['Fractals', 'Self-Similarity', 'Complex Numbers', 'Iteration', 'Escape-Time Algorithm'],
  },

  {
    id: 'gravity-simulator',
    title: 'Gravity Simulator',
    shortDescription: 'Watch N-body gravitational interactions unfold in real time with orbital trails, presets, and interactive body placement.',
    fullDescription: `Gravity is the fundamental force that shapes the cosmos. This N-body gravity simulator lets you witness gravitational interactions firsthand, watching how bodies of different masses attract and orbit one another according to Newton's law of universal gravitation. Explore preset configurations like a simplified solar system, binary stars, or the famous figure-eight three-body solution, or create your own scenarios by clicking to place bodies and dragging to set their velocity.`,
    route: '/visualization/gravity-simulator',
    thumbnail: process.env.PUBLIC_URL + '/images/gravity-simulator-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'gravity', 'orbital mechanics', 'N-body', 'Newton', 'Kepler', 'celestial mechanics'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['double-pendulum', 'projectile-motion-lab', 'pendulum-wave'],
    prerequisites: ['Basic physics concepts'],
    concepts: ['Newton\'s Law of Gravitation', 'Kepler\'s Laws', 'Orbital Mechanics', 'Conservation Laws', 'N-Body Problem'],
  },

  {
    id: 'binary-search-tree',
    title: 'Binary Search Tree',
    shortDescription: 'Build and explore BST and AVL trees interactively with animated insertions, deletions, searches, and rotations.',
    fullDescription: `A Binary Search Tree (BST) is a fundamental data structure that maintains sorted data in a hierarchical tree format. This interactive visualization lets you build and manipulate BSTs step by step, watching how nodes are inserted, searched, and deleted with animated highlights. Toggle AVL mode to see how self-balancing trees maintain optimal height through rotations, and run in-order, pre-order, and post-order traversals with animated node visits.`,
    route: '/visualization/binary-search-tree',
    thumbnail: process.env.PUBLIC_URL + '/images/binary-search-tree-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['data structures', 'binary search tree', 'AVL tree', 'tree traversal', 'algorithms', 'computer science', 'balancing'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['sorting-algorithms', 'pathfinding-algorithms'],
    prerequisites: ['Basic understanding of trees and recursion'],
    concepts: ['Binary Search Tree', 'AVL Tree', 'Tree Rotations', 'Tree Traversals', 'Self-Balancing Trees', 'Time Complexity'],
  },

  {
    id: 'hypothesis-testing',
    title: 'Hypothesis Testing',
    shortDescription: 'Visualize p-values, significance levels, Type I/II errors, and statistical power with interactive distributions.',
    fullDescription: `Hypothesis testing is the backbone of scientific inference. This interactive visualization lets you see how null and alternative distributions overlap, how choosing a significance level affects Type I and Type II error rates, and how sample size and effect size influence statistical power. Drag the test statistic, adjust alpha, and watch as rejection regions, p-values, and decisions update in real time.`,
    route: '/visualization/hypothesis-testing',
    thumbnail: process.env.PUBLIC_URL + '/images/hypothesis-testing-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'hypothesis testing', 'p-value', 'significance', 'power', 'Type I error', 'Type II error'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['central-limit-theorem', 'bayes-theorem'],
    prerequisites: ['Basic understanding of normal distributions'],
    concepts: ['Null Hypothesis', 'Alternative Hypothesis', 'P-Value', 'Statistical Power', 'Type I Error', 'Type II Error'],
  },

  {
    id: 'electromagnetic-fields',
    title: 'Electromagnetic Field Visualizer',
    shortDescription: 'Place electric charges and watch field lines, vectors, and equipotential contours form in real time.',
    fullDescription: `Electric fields are invisible forces that surround every charged particle. This interactive visualization lets you place positive and negative charges on a canvas and instantly see the resulting electric field lines, vector field arrows, and equipotential contours. Drag charges to reshape the field, launch a test charge to trace its path, and explore classic configurations like dipoles, quadrupoles, and parallel plates.`,
    route: '/visualization/electromagnetic-fields',
    thumbnail: process.env.PUBLIC_URL + '/images/electromagnetic-fields-thumbnail.png',
    category: CATEGORIES.ENGINEERING,
    tags: ['physics', 'electromagnetism', 'electric fields', 'Coulomb', 'field lines', 'charges', 'engineering'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['wave-interference', 'fourier-transform'],
    prerequisites: ['Basic understanding of forces and fields'],
    concepts: ['Coulomb\'s Law', 'Electric Field', 'Superposition', 'Field Lines', 'Equipotential Lines'],
  },

  {
    id: 'game-of-life',
    title: 'Conway\'s Game of Life',
    shortDescription: 'Watch complex patterns emerge from simple rules in this classic cellular automaton simulator.',
    fullDescription: `Conway's Game of Life is a zero-player game where complex behavior emerges from just four simple rules applied to cells on a grid. This interactive simulator lets you draw patterns, load famous configurations like gliders, guns, and spaceships, and watch them evolve. Explore how simple local rules produce stunning emergent complexity, from stable still lifes to chaotic growth from tiny seeds.`,
    route: '/visualization/game-of-life',
    thumbnail: process.env.PUBLIC_URL + '/images/game-of-life-thumbnail.png',
    category: CATEGORIES.MATHEMATICS,
    tags: ['mathematics', 'cellular automata', 'emergence', 'Conway', 'simulation', 'patterns', 'complexity'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['fractal-explorer', 'neural-network-training'],
    prerequisites: [],
    concepts: ['Cellular Automata', 'Emergence', 'Self-Organization', 'Turing Completeness', 'Pattern Classification'],
  },

  {
    id: 'predator-prey',
    title: 'Predator-Prey Ecosystem',
    shortDescription: 'Watch wolves hunt rabbits in an agent-based Lotka-Volterra ecosystem with real-time population curves and phase portraits.',
    fullDescription: `The Lotka-Volterra predator-prey model is one of the foundational frameworks in mathematical ecology. This interactive simulator uses an agent-based approach where individual prey (rabbits) move, reproduce, and try to survive while predators (wolves) hunt, gain energy, reproduce, and risk starvation. Watch the classic oscillating population curves emerge from simple individual-level rules, explore phase portraits, and experiment with parameters to find the balance between coexistence and extinction.`,
    route: '/visualization/predator-prey',
    thumbnail: process.env.PUBLIC_URL + '/images/predator-prey-thumbnail.png',
    category: CATEGORIES.BIOLOGY,
    tags: ['biology', 'ecology', 'Lotka-Volterra', 'predator-prey', 'population dynamics', 'simulation', 'agent-based'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['epidemic-sir', 'game-of-life'],
    prerequisites: ['Basic understanding of ecosystems'],
    concepts: ['Lotka-Volterra Equations', 'Population Oscillations', 'Trophic Cascades', 'Phase Portraits', 'Ecological Balance'],
  },

  {
    id: 'markov-chain',
    title: 'Markov Chain Simulator',
    shortDescription: 'Visualize stochastic state transitions, watch visit frequencies converge to the steady-state distribution, and explore classic Markov chain examples.',
    fullDescription: `A Markov chain is a mathematical model describing transitions between states where the next state depends only on the current state. This interactive simulator lets you step through transitions, watch animated particles travel between states, and observe how visit frequencies converge to the theoretical steady-state distribution. Experiment with preset chains including weather models, random walks, the Gambler's Ruin, and Google's PageRank algorithm.`,
    route: '/visualization/markov-chain',
    thumbnail: process.env.PUBLIC_URL + '/images/markov-chain-thumbnail_2.png',
    category: CATEGORIES.PROBABILITY,
    tags: ['probability', 'stochastic processes', 'Markov chains', 'steady state', 'PageRank', 'transition matrix', 'simulation'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['bayes-theorem', 'monty-hall'],
    prerequisites: ['Basic probability concepts'],
    concepts: ['Markov Property', 'Transition Matrices', 'Steady-State Distribution', 'Absorbing States', 'Ergodic Theorem'],
  },

  {
    id: 'lorenz-attractor',
    title: 'Lorenz Attractor',
    shortDescription: 'Explore the iconic butterfly-shaped strange attractor that launched chaos theory, with interactive 3D rotation and parameter tuning.',
    fullDescription: `The Lorenz attractor is one of the most iconic images in mathematics and chaos theory. Discovered by meteorologist Edward Lorenz in 1963 while modeling atmospheric convection, this system of three simple differential equations produces a mesmerizing, never-repeating butterfly-shaped trajectory. Adjust the parameters σ, ρ, and β to see how the attractor morphs, toggle auto-rotation for a stunning 3D view, and witness firsthand how tiny changes lead to dramatically different behavior — the essence of the butterfly effect.`,
    route: '/visualization/lorenz-attractor',
    thumbnail: process.env.PUBLIC_URL + '/images/lorenz-attractor-thumbnail.png',
    category: CATEGORIES.MATHEMATICS,
    tags: ['mathematics', 'chaos theory', 'Lorenz', 'strange attractor', 'butterfly effect', 'dynamical systems', 'differential equations'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['double-pendulum', 'fractal-explorer'],
    prerequisites: ['Basic understanding of 3D space'],
    concepts: ['Strange Attractors', 'Butterfly Effect', 'Sensitive Dependence', 'Dynamical Systems', 'Chaos Theory'],
  },

  {
    id: 'voronoi-diagram',
    title: 'Voronoi Diagram Explorer',
    shortDescription: 'Click to place seed points and watch a colorful Voronoi tessellation form instantly, with Delaunay triangulation overlay.',
    fullDescription: `A Voronoi diagram partitions a plane into regions based on proximity to a set of seed points — every point in a region is closer to its seed than to any other. This interactive explorer lets you click to place points, load preset configurations, toggle Delaunay triangulation (the dual graph), and animate seeds in real time. Discover how this elegant geometric structure appears everywhere from cell biology to airport coverage zones to crystal grain boundaries.`,
    route: '/visualization/voronoi-diagram',
    thumbnail: process.env.PUBLIC_URL + '/images/voronoi-diagram-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['computer science', 'computational geometry', 'Voronoi', 'Delaunay', 'tessellation', 'nearest neighbor', 'algorithms'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['pathfinding-algorithms', 'maze-generation'],
    prerequisites: [],
    concepts: ['Voronoi Tessellation', 'Delaunay Triangulation', 'Nearest Neighbor', 'Computational Geometry', 'Fortune\'s Algorithm'],
  },

  {
    id: 'spring-mass-system',
    title: 'Spring-Mass System',
    shortDescription: 'Drag masses on springs to create oscillations, observe normal modes, and watch energy flow between kinetic and potential forms.',
    fullDescription: `The spring-mass system is a cornerstone of classical mechanics, illustrating harmonic motion, energy conservation, and resonance. This interactive visualization lets you build chains of masses connected by springs, drag them to create displacements, and watch the resulting oscillations. Explore normal modes where all masses move in sync, observe energy transfer between kinetic and potential forms in real time, and experiment with damping to see how oscillations decay.`,
    route: '/visualization/spring-mass-system',
    thumbnail: process.env.PUBLIC_URL + '/images/spring-mass-thumbnail.jpg',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'harmonic motion', 'springs', 'oscillators', 'normal modes', 'energy conservation', 'damping'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-17',
    featured: true,
    relatedVisualizations: ['pendulum-wave', 'wave-interference', 'double-pendulum'],
    prerequisites: ['Basic physics concepts'],
    concepts: ['Hooke\'s Law', 'Simple Harmonic Motion', 'Normal Modes', 'Energy Conservation', 'Damped Oscillations'],
  },

  // Template for adding new visualizations
  /* 
  {
    id: 'unique-identifier',
    title: 'Visualization Title',
    shortDescription: 'Brief description (1-2 sentences) for cards and lists',
    fullDescription: 'Longer description with more details about the visualization',
    route: '/visualization/route-path',
    thumbnail: process.env.PUBLIC_URL + '/images/thumbnail-image.jpg',
    category: CATEGORIES.CATEGORY_NAME,
    tags: ['tag1', 'tag2', 'tag3'],
    difficulty: DIFFICULTY.LEVEL,
    estimatedTime: 'X-Y minutes',
    author: 'Author Name',
    dateAdded: 'YYYY-MM-DD',
    featured: true/false,
    relatedVisualizations: ['related-id-1', 'related-id-2'],
    prerequisites: ['concept1', 'concept2'],
    concepts: ['Concept1', 'Concept2', 'Concept3'],
  },
  */
  {
    id: 'taylor-series',
    title: 'Taylor Series Approximation',
    shortDescription: 'Watch polynomial approximations converge to functions in real time with interactive term controls.',
    fullDescription: `Taylor series are one of the most powerful tools in mathematics, allowing us to approximate
    complex functions with polynomials. This visualization lets you see how adding more terms to the Taylor
    expansion progressively improves the approximation, with support for sin(x), cos(x), e^x, and ln(1+x).`,
    route: '/visualization/taylor-series',
    thumbnail: process.env.PUBLIC_URL + '/images/taylor-series-thumbnail.png',
    category: CATEGORIES.CALCULUS,
    tags: ['calculus', 'taylor series', 'polynomial', 'approximation', 'convergence'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-18',
    featured: true,
    relatedVisualizations: ['derivative-explorer', 'integral-accumulation', 'fourier-transform'],
    prerequisites: ['Basic calculus', 'Derivatives'],
    concepts: ['Taylor expansion', 'Polynomial approximation', 'Radius of convergence', 'Remainder theorem'],
  },
  {
    id: 'genetic-algorithm',
    title: 'Genetic Algorithm Visualizer',
    shortDescription: 'Watch evolution in action as populations of dots evolve to navigate through obstacles.',
    fullDescription: `Genetic algorithms are optimization techniques inspired by natural selection. This visualization
    shows a population of dots evolving over generations to find paths through obstacles. Watch as selection,
    crossover, and mutation drive the population toward increasingly fit solutions.`,
    route: '/visualization/genetic-algorithm',
    thumbnail: process.env.PUBLIC_URL + '/images/genetic-algorithm-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['genetic algorithm', 'evolution', 'optimization', 'AI', 'natural selection'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-18',
    featured: true,
    relatedVisualizations: ['neural-network-training', 'traveling-salesman', 'pathfinding-algorithms'],
    prerequisites: ['Basic programming concepts'],
    concepts: ['Natural selection', 'Crossover', 'Mutation', 'Fitness functions', 'Population dynamics'],
  },
  {
    id: 'orbital-mechanics',
    title: 'Orbital Mechanics Simulator',
    shortDescription: 'Simulate planetary orbits with Newtonian gravity, from simple orbits to chaotic three-body problems.',
    fullDescription: `Explore the beauty of celestial mechanics through this interactive simulator. Watch planets
    orbit under Newtonian gravity, experiment with binary star systems, witness the famous figure-8 three-body
    solution, and create your own orbital systems by clicking to add bodies with custom velocities.`,
    route: '/visualization/orbital-mechanics',
    thumbnail: process.env.PUBLIC_URL + '/images/orbital-mechanics-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['gravity', 'orbits', 'Kepler', 'Newton', 'celestial mechanics', 'n-body'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-18',
    featured: true,
    relatedVisualizations: ['gravity-simulator', 'projectile-motion-lab', 'double-pendulum'],
    prerequisites: ['Basic physics', 'Newton\'s laws'],
    concepts: ['Kepler\'s laws', 'Gravitational force', 'Conservation of energy', 'Angular momentum'],
  },
  {
    id: 'van-der-pauw',
    title: 'Van der Pauw Method',
    shortDescription: 'Explore the Van der Pauw technique for measuring sheet resistance and resistivity of arbitrarily shaped flat samples.',
    fullDescription: `The Van der Pauw method is a fundamental technique in semiconductor and materials characterization. Place four contacts on an arbitrarily shaped flat sample, make two resistance measurements in perpendicular configurations, and solve the transcendental Van der Pauw equation to extract sheet resistance. This visualization lets you experiment with different sample geometries (square, circular, cloverleaf, L-shape), adjust material properties and contact sizes, and watch how measurement accuracy depends on experimental conditions.`,
    route: '/visualization/van-der-pauw',
    thumbnail: process.env.PUBLIC_URL + '/images/van-der-pauw-thumbnail.png',
    category: CATEGORIES.PHYSICS,
    tags: ['physics', 'resistivity', 'sheet resistance', 'semiconductor', 'four-point probe', 'thin film'],
    difficulty: DIFFICULTY.ADVANCED,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-21',
    featured: false,
    relatedVisualizations: ['electromagnetic-fields', 'wave-interference'],
    prerequisites: ['Basic electromagnetism', 'Ohm\'s law', 'Resistivity concepts'],
    concepts: ['Sheet Resistance', 'Four-Point Measurement', 'Van der Pauw Equation', 'Contact Resistance'],
  },
  {
    id: 'diffusion-simulation',
    title: 'Diffusion & Brownian Motion',
    shortDescription: 'Visualize particle diffusion and Brownian motion with real-time concentration tracking.',
    fullDescription: `Watch particles undergo random Brownian motion and observe how diffusion spreads them from
    concentrated regions to fill available space. This simulation includes free diffusion, concentration gradients
    with membranes, temperature controls, and real-time tracking of mean squared displacement.`,
    route: '/visualization/diffusion-simulation',
    thumbnail: process.env.PUBLIC_URL + '/images/diffusion-thumbnail.jpg',
    category: CATEGORIES.CHEMISTRY,
    tags: ['diffusion', 'Brownian motion', 'particles', 'thermodynamics', 'osmosis'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-18',
    featured: false,
    relatedVisualizations: ['reaction-kinetics', 'epidemic-sir', 'predator-prey'],
    prerequisites: ['Basic chemistry'],
    concepts: ['Fick\'s laws', 'Brownian motion', 'Mean squared displacement', 'Temperature and kinetic energy'],
  },
  {
    id: 'chaos-game',
    title: 'Chaos Game Fractal Generator',
    shortDescription: 'Generate stunning fractal patterns by iteratively jumping toward random polygon vertices.',
    fullDescription: `The Chaos Game is a deceptively simple algorithm that produces breathtaking fractal patterns.
    Starting from a random point, repeatedly jump a fraction of the distance toward a randomly chosen vertex of
    a polygon. Watch as thousands of points reveal hidden structure: Sierpinski triangles, pentagonal fractals,
    and more emerge from pure randomness.`,
    route: '/visualization/chaos-game',
    thumbnail: process.env.PUBLIC_URL + '/images/chaos-game-thumbnail.png',
    category: CATEGORIES.MATHEMATICS,
    tags: ['fractals', 'chaos', 'Sierpinski', 'IFS', 'self-similarity', 'mathematics'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2025-03-18',
    featured: true,
    relatedVisualizations: ['fractal-explorer', 'lorenz-attractor', 'game-of-life'],
    prerequisites: ['None'],
    concepts: ['Iterated function systems', 'Self-similarity', 'Fractal dimension', 'Attractors'],
  },
  {
    id: 'gradient-descent',
    title: 'Gradient Descent Visualizer',
    shortDescription: 'Watch SGD, Momentum, RMSProp, and Adam race down loss surfaces to find the minimum.',
    fullDescription: `Gradient descent is the workhorse optimization algorithm behind modern machine learning. This interactive visualizer renders a color-mapped loss surface and simultaneously runs four optimizers — SGD, SGD with Momentum, RMSProp, and Adam — so you can compare their convergence paths in real time. Adjust the learning rate, switch between loss functions (bowl, Rosenbrock, Himmelblau, Rastrigin), and observe how each optimizer handles saddle points, narrow valleys, and local minima.`,
    route: '/visualization/gradient-descent',
    thumbnail: process.env.PUBLIC_URL + '/images/gradient-descent-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['machine learning', 'optimization', 'gradient descent', 'Adam', 'SGD', 'neural networks', 'data science'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-21',
    featured: true,
    relatedVisualizations: ['neural-network-training', 'genetic-algorithm'],
    prerequisites: ['Basic calculus', 'Partial derivatives'],
    concepts: ['Gradient Descent', 'Learning Rate', 'Momentum', 'Adaptive Learning Rates', 'Loss Surfaces'],
  },
  {
    id: 'monte-carlo',
    title: 'Monte Carlo Simulation',
    shortDescription: 'Estimate π, integrate functions, and drop Buffon\'s needles using random sampling with live convergence tracking.',
    fullDescription: `Monte Carlo methods use random sampling to solve problems that might be deterministic in principle. This interactive visualizer offers three classic demonstrations: estimating π by throwing darts at a circle, computing integrals via random sampling, and simulating Buffon's needle problem. Watch thousands of samples accumulate in real time and observe how the estimate converges to the true value — a beautiful demonstration of the law of large numbers.`,
    route: '/visualization/monte-carlo',
    thumbnail: process.env.PUBLIC_URL + '/images/monte-carlo-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'Monte Carlo', 'random sampling', 'pi estimation', 'integration', 'probability', 'data science'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-21',
    featured: true,
    relatedVisualizations: ['central-limit-theorem', 'hypothesis-testing', 'bayes-theorem'],
    prerequisites: ['Basic probability'],
    concepts: ['Random Sampling', 'Law of Large Numbers', 'Convergence Rate', 'Numerical Integration', 'Buffon\'s Needle'],
  },
  {
    id: 'pca-explorer',
    title: 'PCA / Dimensionality Reduction',
    shortDescription: 'Generate 3D data clusters, compute principal components, and animate the projection to 2D.',
    fullDescription: `Principal Component Analysis (PCA) is one of the most important techniques in data science for reducing high-dimensional data to its most informative dimensions. This interactive explorer generates 3D data clusters, computes the covariance matrix and its eigenvectors, and animates the projection from 3D to 2D along the principal components. See how variance is preserved, which directions matter most, and why dimensionality reduction works.`,
    route: '/visualization/pca-explorer',
    thumbnail: process.env.PUBLIC_URL + '/images/pca-explorer-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'PCA', 'dimensionality reduction', 'eigenvectors', 'covariance', 'data science', 'machine learning'],
    difficulty: DIFFICULTY.ADVANCED,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-21',
    featured: true,
    relatedVisualizations: ['matrix-transformation', 'eigen-geometry-explorer', 'neural-network-training'],
    prerequisites: ['Linear algebra basics', 'Eigenvalues and eigenvectors'],
    concepts: ['Principal Components', 'Covariance Matrix', 'Eigendecomposition', 'Variance Explained', 'Data Projection'],
  },
  {
    id: 'bayesian-updating',
    title: 'Bayesian Updating Live',
    shortDescription: 'Watch prior distributions transform into posteriors as data arrives one observation at a time.',
    fullDescription: `Bayesian inference is the process of updating beliefs in light of new evidence. This interactive visualizer shows the prior, likelihood, and posterior distributions as smooth curves that evolve with each new observation. Estimate a coin's bias using the Beta-Binomial conjugate model, or infer a normal mean — watching the posterior sharpen and converge to the true value as data accumulates. A beautiful demonstration of how uncertainty decreases with evidence.`,
    route: '/visualization/bayesian-updating',
    thumbnail: process.env.PUBLIC_URL + '/images/bayesian-updating-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'Bayesian inference', 'prior', 'posterior', 'Beta distribution', 'conjugate priors', 'data science'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-21',
    featured: true,
    relatedVisualizations: ['bayes-theorem', 'hypothesis-testing', 'central-limit-theorem'],
    prerequisites: ['Basic probability', 'Bayes\' theorem'],
    concepts: ['Prior Distribution', 'Posterior Distribution', 'Conjugate Priors', 'Beta-Binomial Model', 'Credible Intervals'],
  },

  // ── New Data Science Visualizations ───────────────────────────────────────

  {
    id: 'k-means-clustering',
    title: 'K-Means Clustering',
    shortDescription: 'Watch centroids converge as K-Means partitions data into clusters step by step.',
    fullDescription: `K-Means is one of the most fundamental clustering algorithms in machine learning. This interactive visualization lets you generate datasets with different shapes, choose K, and watch the algorithm iteratively assign points to clusters and update centroids until convergence. Compare random initialization with K-Means++ and build intuition for how the algorithm finds structure in data.`,
    route: '/visualization/k-means-clustering',
    thumbnail: process.env.PUBLIC_URL + '/images/k-means-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['machine learning', 'clustering', 'unsupervised learning', 'data science', 'k-means', 'centroids'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['pca-explorer', 'neural-network-training'],
    prerequisites: ['Basic understanding of distance metrics'],
    concepts: ['Clustering', 'Centroids', 'K-Means++', 'Convergence', 'Voronoi Regions'],
  },
  {
    id: 'decision-tree',
    title: 'Decision Tree Builder',
    shortDescription: 'Build a decision tree step by step and watch how recursive splits create decision boundaries.',
    fullDescription: `Decision trees are interpretable machine learning models that make predictions by learning a hierarchy of if-then rules from data. This visualization lets you generate 2D datasets, then watch a CART decision tree grow split by split — seeing how Gini impurity or information gain guides each partition. Control tree depth, observe overfitting, and understand the connection between the tree structure and its decision boundaries.`,
    route: '/visualization/decision-tree',
    thumbnail: process.env.PUBLIC_URL + '/images/decision-tree-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['machine learning', 'decision tree', 'classification', 'data science', 'CART', 'Gini impurity'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['k-means-clustering', 'neural-network-training'],
    prerequisites: ['Basic probability'],
    concepts: ['Decision Trees', 'Gini Impurity', 'Information Gain', 'Overfitting', 'Pruning'],
  },
  {
    id: 'k-nearest-neighbors',
    title: 'K-Nearest Neighbors',
    shortDescription: 'Place points, adjust K, and watch decision boundaries morph in real-time.',
    fullDescription: `K-Nearest Neighbors (KNN) is one of the simplest yet most powerful classification algorithms. This visualization lets you place data points of different classes, adjust K, and instantly see how the decision boundary changes. Explore different distance metrics, understand the bias-variance tradeoff with K, and see how KNN makes predictions by majority vote of neighbors.`,
    route: '/visualization/k-nearest-neighbors',
    thumbnail: process.env.PUBLIC_URL + '/images/knn-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['machine learning', 'classification', 'KNN', 'data science', 'instance-based learning'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['k-means-clustering', 'decision-tree'],
    prerequisites: ['Basic understanding of distance'],
    concepts: ['Instance-Based Learning', 'Distance Metrics', 'Decision Boundaries', 'Bias-Variance with K'],
  },
  {
    id: 'linear-regression',
    title: 'Linear & Logistic Regression',
    shortDescription: 'Fit lines and curves to data with gradient descent, visualize residuals, and explore the sigmoid boundary.',
    fullDescription: `Regression is the backbone of predictive modeling. This dual-mode visualization shows linear regression (fitting polynomials to continuous data) and logistic regression (classifying with a sigmoid boundary). Watch gradient descent optimize the model in real-time, see residuals shrink, and understand how polynomial degree affects overfitting.`,
    route: '/visualization/linear-regression',
    thumbnail: process.env.PUBLIC_URL + '/images/linear-regression-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'regression', 'machine learning', 'data science', 'gradient descent', 'sigmoid'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['gradient-descent', 'bias-variance'],
    prerequisites: ['Basic algebra', 'Function graphing'],
    concepts: ['Least Squares', 'Gradient Descent', 'Logistic Function', 'Residuals', 'Overfitting'],
  },
  {
    id: 'bias-variance',
    title: 'Bias-Variance Tradeoff',
    shortDescription: 'Visualize underfitting and overfitting by sampling data, fitting models, and decomposing prediction error.',
    fullDescription: `The bias-variance tradeoff is a central concept in machine learning that explains why models can fail — either by being too simple (high bias, underfitting) or too complex (high variance, overfitting). This visualization lets you generate noisy data, fit polynomial models of varying complexity, run many trials to see variance, and watch the classic U-shaped total error curve emerge.`,
    route: '/visualization/bias-variance',
    thumbnail: process.env.PUBLIC_URL + '/images/bias-variance-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'machine learning', 'bias', 'variance', 'overfitting', 'underfitting', 'data science'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['linear-regression', 'decision-tree'],
    prerequisites: ['Basic statistics', 'Polynomial functions'],
    concepts: ['Bias', 'Variance', 'Overfitting', 'Underfitting', 'Model Complexity', 'Cross-Validation'],
  },
  {
    id: 'anscombe-quartet',
    title: "Correlation & Anscombe's Quartet",
    shortDescription: "See why visualization matters: four datasets with identical statistics but wildly different shapes.",
    fullDescription: `Anscombe's Quartet is a famous collection of four datasets that share nearly identical summary statistics (mean, variance, correlation, regression line) yet look completely different when plotted. This visualization brings the lesson to life with animated reveals and an interactive correlation explorer where you can place points, add outliers, and watch statistics change in real-time.`,
    route: '/visualization/anscombe-quartet',
    thumbnail: process.env.PUBLIC_URL + '/images/anscombe-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'correlation', 'data visualization', 'data science', 'outliers', 'regression'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['linear-regression', 'hypothesis-testing'],
    prerequisites: ['Basic statistics'],
    concepts: ['Correlation Coefficient', 'Summary Statistics', 'Data Visualization', 'Outlier Effects'],
  },
  {
    id: 'kernel-density',
    title: 'Kernel Density Estimation',
    shortDescription: 'Build smooth density curves from data by summing individual kernels and tuning bandwidth.',
    fullDescription: `Kernel Density Estimation (KDE) is a non-parametric way to estimate the probability density function of a dataset. This visualization shows how placing a small "bump" (kernel) at each data point and summing them produces a smooth density estimate. Adjust bandwidth to see the critical tradeoff between smoothness and detail, compare kernel types, and understand why KDE is preferred over histograms for continuous data.`,
    route: '/visualization/kernel-density',
    thumbnail: process.env.PUBLIC_URL + '/images/kde-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'density estimation', 'KDE', 'bandwidth', 'data science', 'nonparametric'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['distribution-explorer', 'central-limit-theorem'],
    prerequisites: ['Basic probability distributions'],
    concepts: ['Kernel Functions', 'Bandwidth Selection', 'Density Estimation', 'Smoothing'],
  },
  {
    id: 'ab-testing',
    title: 'A/B Testing Simulator',
    shortDescription: 'Run experiments, track p-values in real-time, and discover the dangers of peeking at results.',
    fullDescription: `A/B testing is the gold standard for making data-driven decisions in tech, medicine, and business. This simulator lets you set true conversion rates, run experiments with accumulating samples, and watch p-values evolve. Discover how sample size affects power, why "peeking" at results inflates false positives, and run hundreds of experiments to verify that Type I error rates match theory.`,
    route: '/visualization/ab-testing',
    thumbnail: process.env.PUBLIC_URL + '/images/ab-testing-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'A/B testing', 'hypothesis testing', 'p-value', 'data science', 'experimentation'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['hypothesis-testing', 'central-limit-theorem'],
    prerequisites: ['Basic probability', 'Hypothesis testing concepts'],
    concepts: ['Statistical Significance', 'P-Values', 'Type I/II Errors', 'Statistical Power', 'P-Hacking'],
  },
  {
    id: 'survival-analysis',
    title: 'Survival Analysis (Kaplan-Meier)',
    shortDescription: 'Plot step-function survival curves, handle censored data, and compare groups with the log-rank test.',
    fullDescription: `Survival analysis models time-to-event data — how long until something happens. The Kaplan-Meier estimator produces step-function survival curves that elegantly handle censored observations (subjects who haven't experienced the event yet). This visualization generates synthetic clinical trial data, animates the curve construction, and computes log-rank tests to compare groups.`,
    route: '/visualization/survival-analysis',
    thumbnail: process.env.PUBLIC_URL + '/images/survival-analysis-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'survival analysis', 'Kaplan-Meier', 'censoring', 'data science', 'clinical trials'],
    difficulty: DIFFICULTY.ADVANCED,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['hypothesis-testing', 'ab-testing'],
    prerequisites: ['Basic statistics', 'Probability distributions'],
    concepts: ['Survival Function', 'Kaplan-Meier Estimator', 'Censoring', 'Log-Rank Test', 'Hazard Rate'],
  },
  {
    id: 'dim-reduction-showdown',
    title: 'Dimensionality Reduction Showdown',
    shortDescription: 'Compare PCA, t-SNE, and UMAP side-by-side as they reduce high-dimensional clusters to 2D.',
    fullDescription: `When data lives in many dimensions, we need ways to visualize it in 2D. This showdown runs three methods side-by-side on the same dataset: PCA (fast, linear), t-SNE (preserves local structure), and UMAP (balances local and global). Watch t-SNE and UMAP animate their optimization as clusters emerge, and understand when each method shines.`,
    route: '/visualization/dim-reduction-showdown',
    thumbnail: process.env.PUBLIC_URL + '/images/dim-reduction-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'PCA', 't-SNE', 'UMAP', 'dimensionality reduction', 'data science', 'machine learning'],
    difficulty: DIFFICULTY.ADVANCED,
    estimatedTime: '15-25 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['pca-explorer', 'k-means-clustering'],
    prerequisites: ['Linear algebra basics', 'PCA concepts'],
    concepts: ['PCA', 't-SNE', 'UMAP', 'Perplexity', 'KL Divergence', 'Nearest Neighbor Graphs'],
  },
  {
    id: 'feature-scaling',
    title: 'Feature Scaling Visualizer',
    shortDescription: 'Compare Min-Max, Z-score, and Robust scaling and see how they transform data and affect algorithms.',
    fullDescription: `Feature scaling is a critical preprocessing step in machine learning. When features have vastly different scales, distance-based algorithms and gradient descent can fail. This visualization shows how four scaling methods (raw, min-max, standardization, robust) transform the same dataset, how they handle outliers differently, and how scaling affects gradient descent convergence.`,
    route: '/visualization/feature-scaling',
    thumbnail: process.env.PUBLIC_URL + '/images/feature-scaling-thumbnail.png',
    category: CATEGORIES.STATISTICS,
    tags: ['statistics', 'feature scaling', 'normalization', 'standardization', 'data science', 'preprocessing'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '10-15 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['linear-regression', 'k-nearest-neighbors', 'gradient-descent'],
    prerequisites: ['Basic statistics (mean, standard deviation)'],
    concepts: ['Min-Max Scaling', 'Standardization', 'Robust Scaling', 'Outlier Resistance'],
  },
  {
    id: 'distribution-explorer',
    title: 'Distribution Explorer',
    shortDescription: 'Visualize 8 fundamental probability distributions, adjust parameters, sample, and compute probabilities.',
    fullDescription: `Probability distributions are the foundation of statistics. This interactive explorer lets you visualize Normal, Uniform, Exponential, Binomial, Poisson, Beta, Gamma, and Chi-Squared distributions with adjustable parameters. Watch PDFs morph as you drag sliders, shade probability regions, draw random samples that converge to the theoretical curve, and compare distributions side-by-side.`,
    route: '/visualization/distribution-explorer',
    thumbnail: process.env.PUBLIC_URL + '/images/distribution-explorer-thumbnail.png',
    category: CATEGORIES.PROBABILITY,
    tags: ['probability', 'distributions', 'normal', 'Poisson', 'Beta', 'data science', 'statistics'],
    difficulty: DIFFICULTY.BEGINNER,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['central-limit-theorem', 'bayesian-updating', 'kernel-density'],
    prerequisites: ['Basic probability'],
    concepts: ['PDF', 'CDF', 'Parameters', 'Sampling', 'Continuous vs Discrete Distributions'],
  },
  {
    id: 'random-walk',
    title: 'Random Walk & Brownian Motion',
    shortDescription: 'Simulate 1D and 2D random walks and geometric Brownian motion for stock price modeling.',
    fullDescription: `A random walk is one of the most fundamental stochastic processes — a path formed by successive random steps. This visualization offers three modes: 1D walks showing how the distribution of final positions approaches normal, 2D walks with colorful trails demonstrating diffusion, and geometric Brownian motion modeling stock prices with drift and volatility. See the deep connections between randomness, diffusion, and finance.`,
    route: '/visualization/random-walk',
    thumbnail: process.env.PUBLIC_URL + '/images/random-walk-thumbnail.png',
    category: CATEGORIES.PROBABILITY,
    tags: ['probability', 'random walk', 'Brownian motion', 'stochastic processes', 'data science', 'finance'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['diffusion-simulation', 'monte-carlo', 'central-limit-theorem'],
    prerequisites: ['Basic probability'],
    concepts: ['Random Walk', 'Brownian Motion', 'Diffusion', 'Geometric Brownian Motion', 'Drift and Volatility'],
  },
  {
    id: 'network-graph',
    title: 'Network Graph Visualizer',
    shortDescription: 'Explore force-directed graphs, compute centrality measures, and detect communities.',
    fullDescription: `Networks (graphs) are everywhere — social networks, the internet, biological systems, and transportation. This visualization uses a force-directed physics simulation to lay out graphs, letting you generate different network types (random, scale-free, small-world), compute centrality measures to find important nodes, and detect community structure through label propagation.`,
    route: '/visualization/network-graph',
    thumbnail: process.env.PUBLIC_URL + '/images/network-graph-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['computer science', 'graph theory', 'networks', 'centrality', 'community detection', 'data science'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '15-20 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['pagerank', 'pathfinding-algorithms'],
    prerequisites: ['Basic graph concepts'],
    concepts: ['Force-Directed Layout', 'Degree Centrality', 'Betweenness Centrality', 'Community Detection'],
  },
  {
    id: 'pagerank',
    title: 'PageRank Algorithm',
    shortDescription: "Visualize Google's original algorithm: watch rank flow through a web graph and converge.",
    fullDescription: `PageRank is the algorithm that powered Google's original search engine. It models a "random surfer" clicking links and computes the long-run probability of visiting each page. This visualization shows a directed web graph where node sizes represent PageRank scores, animates the iterative computation, and lets you simulate the random surfer to verify that visit frequencies converge to the theoretical ranks.`,
    route: '/visualization/pagerank',
    thumbnail: process.env.PUBLIC_URL + '/images/pagerank-thumbnail.png',
    category: CATEGORIES.COMPUTER_SCIENCE,
    tags: ['computer science', 'PageRank', 'Google', 'graph algorithms', 'data science', 'web search'],
    difficulty: DIFFICULTY.INTERMEDIATE,
    estimatedTime: '12-18 minutes',
    author: 'SciVizHub Team',
    dateAdded: '2026-03-28',
    featured: true,
    relatedVisualizations: ['network-graph', 'markov-chain'],
    prerequisites: ['Basic graph concepts', 'Probability'],
    concepts: ['PageRank', 'Random Surfer Model', 'Damping Factor', 'Convergence', 'Transition Matrix'],
  },
];

// Utility functions to work with the visualization registry

/**
 * Get all visualizations
 * @returns {Array} All visualizations
 */
export const getAllVisualizations = () => visualizations;

/**
 * Get featured visualizations for homepage
 * @param {number} limit - Maximum number of visualizations to return
 * @returns {Array} Featured visualizations
 */
export const getFeaturedVisualizations = (limit = 3) => {
  // Get all visualizations
  const allVisualizations = [...visualizations];
  
  // Shuffle the array using Fisher-Yates algorithm
  for (let i = allVisualizations.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allVisualizations[i], allVisualizations[j]] = [allVisualizations[j], allVisualizations[i]];
  }
  
  // Return the first 'limit' visualizations
  return allVisualizations.slice(0, limit);
};

/**
 * Get visualizations by category
 * @param {string} category - Category to filter by
 * @returns {Array} Visualizations in the given category
 */
export const getVisualizationsByCategory = (category) => {
  return visualizations.filter(viz => viz.category === category);
};

/**
 * Get a visualization by its ID
 * @param {string} id - Visualization ID
 * @returns {Object|null} Visualization object or null if not found
 */
export const getVisualizationById = (id) => {
  return visualizations.find(viz => viz.id === id) || null;
};

/**
 * Get related visualizations
 * @param {string} id - Visualization ID to find related content for
 * @returns {Array} Related visualizations
 */
export const getRelatedVisualizations = (id) => {
  const visualization = getVisualizationById(id);
  if (!visualization) return [];
  
  return visualization.relatedVisualizations
    .map(relatedId => getVisualizationById(relatedId))
    .filter(Boolean); // Remove null values
};

/**
 * Search visualizations by search term
 * @param {string} searchTerm - Term to search for
 * @returns {Array} Matching visualizations
 */
export const searchVisualizations = (searchTerm) => {
  if (!searchTerm) return [];
  
  const term = searchTerm.toLowerCase();
  return visualizations.filter(viz => 
    viz.title.toLowerCase().includes(term) ||
    viz.shortDescription.toLowerCase().includes(term) ||
    viz.fullDescription.toLowerCase().includes(term) ||
    viz.tags.some(tag => tag.toLowerCase().includes(term))
  );
};

export default visualizations; 
